class DcPackFinder extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-guests]')];
    this.result = this.querySelector('[data-pack-result]');
    this.link = this.querySelector('[data-pack-link]');
    this.buttons.forEach((button) => button.addEventListener('click', () => this.select(button)));
  }

  select(button) {
    this.buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    const guests = Number(button.dataset.guests);
    const label = button.dataset.label || `${guests} personas`;
    const url = button.dataset.url;
    if (this.result) this.result.textContent = `Te recomendamos comenzar con un pack para ${label}.`;
    if (this.link && url) {
      this.link.href = url;
      this.link.hidden = false;
    }
  }
}

class DcHeliumCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.result = this.querySelector('[data-helium-result]');
    if (!this.form || !this.result) return;
    this.form.addEventListener('input', () => this.calculate());
    this.calculate();
  }

  calculate() {
    const quantity = Math.max(1, Number(this.form.elements.quantity.value) || 1);
    const type = this.form.elements.balloon_type.value;
    const yields = { latex9: 30, latex12: 20, foil18: 15 };
    const perTank = yields[type] || 20;
    const tanks = Math.ceil(quantity / perTank);
    const labels = { latex9: 'látex de 9"', latex12: 'látex de 12"', foil18: 'foil de 18"' };
    this.result.innerHTML = `<strong>${tanks} ${tanks === 1 ? 'tanque de 7 L' : 'tanques de 7 L'}</strong><p>Estimación para ${quantity} globos ${labels[type]}. El rendimiento real puede variar según el llenado y la temperatura.</p>`;
  }
}

if (!customElements.get('dc-pack-finder')) customElements.define('dc-pack-finder', DcPackFinder);
if (!customElements.get('dc-helium-calculator')) customElements.define('dc-helium-calculator', DcHeliumCalculator);

class DcHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-dc-slide]')];
    this.dots = [...this.querySelectorAll('[data-dc-dot]')];
    this.current = 0;
    this.querySelector('[data-dc-prev]')?.addEventListener('click', () => this.show(this.current - 1, true));
    this.querySelector('[data-dc-next]')?.addEventListener('click', () => this.show(this.current + 1, true));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.start();
  }

  show(index, restart = false) {
    if (this.slides.length < 2) return;
    this.current = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.current;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.current;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });
    if (restart) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.current + 1), Number(this.dataset.delay) || 6000);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
  }
}

if (!customElements.get('dc-hero-carousel')) customElements.define('dc-hero-carousel', DcHeroCarousel);

const initDcDesktopMenus = () => {
  if (!window.matchMedia('(min-width: 990px)').matches) return;

  const menus = [...document.querySelectorAll('.header__inline-menu header-menu')];
  if (!menus.length) return;

  const closeOthers = (current) => {
    menus.forEach((menu) => {
      if (menu === current) return;
      window.clearTimeout(menu.dcCloseTimer);
      const details = menu.querySelector(':scope > details');
      details?.removeAttribute('open');
      details?.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  menus.forEach((menu) => {
    if (menu.dataset.dcMenuReady === 'true') return;
    menu.dataset.dcMenuReady = 'true';
    const details = menu.querySelector(':scope > details');
    const summary = details?.querySelector(':scope > summary');
    if (!details || !summary) return;

    const open = () => {
      window.clearTimeout(menu.dcCloseTimer);
      closeOthers(menu);
      details.setAttribute('open', '');
      summary.setAttribute('aria-expanded', 'true');
      const header = menu.closest('sticky-header');
      if (header) header.preventHide = true;
    };

    const scheduleClose = () => {
      window.clearTimeout(menu.dcCloseTimer);
      menu.dcCloseTimer = window.setTimeout(() => {
        if (menu.matches(':hover') || menu.contains(document.activeElement)) return;
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
        const header = menu.closest('sticky-header');
        if (header) header.preventHide = false;
      }, 450);
    };

    menu.addEventListener('pointerenter', open);
    menu.addEventListener('pointerleave', scheduleClose);
    menu.addEventListener('focusin', open);
    menu.addEventListener('focusout', scheduleClose);
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      details.removeAttribute('open');
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDcDesktopMenus);
} else {
  initDcDesktopMenus();
}

const initDcCustomMegaMenus = (scope = document) => {
  if (!window.matchMedia('(min-width: 990px)').matches) return;

  const menus = [...scope.querySelectorAll('.dc-mega-menu')];
  const closeOthers = (current) => {
    document.querySelectorAll('.dc-mega-menu.is-open').forEach((menu) => {
      if (menu !== current) menu.classList.remove('is-open');
    });
  };

  menus.forEach((menu) => {
    if (menu.dataset.dcHoverReady === 'true') return;
    menu.dataset.dcHoverReady = 'true';
    const trigger = menu.querySelector(':scope > .dc-mega-menu__trigger');

    const open = () => {
      window.clearTimeout(menu.dcCloseTimer);
      closeOthers(menu);
      menu.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
    };

    const scheduleClose = () => {
      window.clearTimeout(menu.dcCloseTimer);
      menu.dcCloseTimer = window.setTimeout(() => {
        if (menu.matches(':hover') || menu.contains(document.activeElement)) return;
        menu.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
      }, 650);
    };

    menu.addEventListener('pointerenter', open);
    menu.addEventListener('pointerleave', scheduleClose);
    menu.addEventListener('focusin', open);
    menu.addEventListener('focusout', scheduleClose);
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      window.clearTimeout(menu.dcCloseTimer);
      menu.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus();
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDcCustomMegaMenus());
} else {
  initDcCustomMegaMenus();
}
document.addEventListener('shopify:section:load', (event) => initDcCustomMegaMenus(event.target));

const initDcWhatsAppLinks = (scope = document) => {
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  const message = 'Hola%2C%20necesito%20ayuda%20para%20comprar%20en%20Don%20Cotill%C3%B3n';
  scope.querySelectorAll('[data-dc-whatsapp-link]').forEach((link) => {
    link.href = `whatsapp://send?phone=56966946797&text=${message}`;
    link.removeAttribute('target');
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDcWhatsAppLinks());
} else {
  initDcWhatsAppLinks();
}
document.addEventListener('shopify:section:load', (event) => initDcWhatsAppLinks(event.target));

const initDcStickyAddToCart = () => {
  const originalButton = document.querySelector('.product-form__submit');
  const productInfo = document.querySelector('.product__info-container');
  if (!originalButton || !productInfo || document.querySelector('.dc-sticky-atc')) return;

  const title = document.querySelector('.product__title h1')?.textContent?.trim() || document.title;
  const price = document.querySelector('.product__info-container .price')?.textContent?.trim() || '';
  const bar = document.createElement('div');
  bar.className = 'dc-sticky-atc';
  bar.setAttribute('aria-hidden', 'true');

  const meta = document.createElement('div');
  meta.className = 'dc-sticky-atc__meta';
  const titleNode = document.createElement('strong');
  titleNode.textContent = title;
  const priceNode = document.createElement('span');
  priceNode.textContent = price;
  meta.append(titleNode, priceNode);

  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'dc-button';
  action.textContent = originalButton.textContent.trim();
  action.disabled = originalButton.disabled;
  action.addEventListener('click', () => originalButton.click());
  bar.append(meta, action);
  document.body.append(bar);

  const sync = () => {
    action.disabled = originalButton.disabled;
    action.textContent = originalButton.textContent.trim();
    const currentPrice = document.querySelector('.product__info-container .price')?.textContent?.trim();
    if (currentPrice) priceNode.textContent = currentPrice;
  };
  new MutationObserver(sync).observe(productInfo, { subtree: true, childList: true, characterData: true, attributes: true });

  const observer = new IntersectionObserver(([entry]) => {
    const visible = !entry.isIntersecting && window.matchMedia('(max-width: 749px)').matches;
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', String(!visible));
  }, { threshold: 0 });
  observer.observe(originalButton);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDcStickyAddToCart);
} else {
  initDcStickyAddToCart();
}
